import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { toast } from 'react-toastify';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Tooltip from '@material-ui/core/Tooltip';
import Alert from '@mui/material/Alert';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import Container from '../../GeneralComponents/Container';
import { Topbar, Sidebar, Table } from '../components';
import setGlobalLoader from '../../../redux/actions/loaderActions';
import {
    getBatchById,
    assignStudentToBatch,
    removeStudentFromBatch,
    getStudents,
} from '../../../redux/actions/apiActions';

const DetailRow = ({ label, value }) => (
    <>
        <Box display="flex" justifyContent="space-between" alignItems="center" padding={2}>
            <Typography variant="subtitle2" fontWeight={700}>{label}</Typography>
            <Typography variant="subtitle2">{value ?? '—'}</Typography>
        </Box>
        <Divider />
    </>
);

const ViewBatch = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const params = useParams();
    const theme = useTheme();
    const isMd = useMediaQuery(theme.breakpoints.up('md'), { defaultMatches: true });

    const [openSidebar, setOpenSidebar] = useState(false);
    const open = isMd ? false : openSidebar;

    const [batch, setBatch] = useState(null);
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);

    const [assignDialog, setAssignDialog] = useState(false);
    const [allStudents, setAllStudents] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [assigning, setAssigning] = useState(false);

    const fetchBatch = async () => {
        setLoading(true);
        try {
            const res = await getBatchById(params.id);
            setBatch(res?.data?.data?.batch);
            setStudents(res?.data?.data?.students || []);
        } catch (error) {
            toast.error('Error loading batch');
            navigate('/batches');
        } finally {
            setLoading(false);
        }
    };

    const fetchAllStudents = async () => {
        try {
            const res = await getStudents('?limit=10000');
            const options = (res?.data?.data?.docs || [])
                .filter((s) => s.status !== 'COMPLETED')
                .map((s) => ({ value: s._id, label: `${s.name} (${s.phoneNumber})` }));
            setAllStudents(options);
        } catch (error) {
            console.log('fetch students error', error);
        }
    };

    useEffect(() => {
        fetchBatch();
    }, [params.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleOpenAssign = () => {
        fetchAllStudents();
        setSelectedStudent(null);
        setAssignDialog(true);
    };

    const handleAssign = async () => {
        if (!selectedStudent) return;
        setAssigning(true);
        dispatch(setGlobalLoader(true));
        try {
            await assignStudentToBatch(params.id, selectedStudent.value);
            toast.success('Student assigned to batch');
            setAssignDialog(false);
            fetchBatch();
        } catch (error) {
            toast.error(error?.response?.data?.message ?? 'Error assigning student');
        } finally {
            setAssigning(false);
            dispatch(setGlobalLoader(false));
        }
    };

    const handleRemove = async (studentId) => {
        if (!window.confirm('Remove this student from the batch?')) return;
        dispatch(setGlobalLoader(true));
        try {
            await removeStudentFromBatch(params.id, studentId);
            toast.success('Student removed from batch');
            fetchBatch();
        } catch (error) {
            toast.error(error?.response?.data?.message ?? 'Error removing student');
        } finally {
            dispatch(setGlobalLoader(false));
        }
    };

    const studentColumns = [
        { title: 'Sr No.', field: 'index', filtering: false },
        { title: 'Name', field: 'name', filtering: false },
        { title: 'Phone', field: 'phoneNumber', filtering: false },
        { title: 'Status', field: 'status', filtering: false },
        {
            title: 'Actions',
            field: '',
            filtering: false,
            render: (rowData) => (
                <Tooltip title="Remove from batch">
                    <PersonRemoveIcon
                        style={{ cursor: 'pointer', color: '#d32f2f' }}
                        onClick={() => handleRemove(rowData._id)}
                    />
                </Tooltip>
            ),
        },
    ];

    const studentRows = students.map((s, i) => ({ ...s, index: i + 1 }));

    return (
        <Box>
            <AppBar
                position="fixed"
                sx={{
                    backgroundColor: theme.palette.background.paper,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
                elevation={0}
            >
                <Container maxWidth={1} paddingY={{ xs: 1, sm: 1.5 }}>
                    <Topbar onSidebarOpen={() => setOpenSidebar(true)} />
                </Container>
            </AppBar>
            <Sidebar
                onClose={() => setOpenSidebar(false)}
                open={open}
                variant={isMd ? 'permanent' : 'temporary'}
            />
            <main>
                <Box height={{ xs: 94, sm: 102, md: 107 }} />
                <Box
                    display="flex"
                    flex="1 1 auto"
                    overflow="hidden"
                    paddingLeft={{ md: '256px' }}
                >
                    <Box display="flex" flex="1 1 auto" overflow="hidden">
                        <Box flex="1 1 auto" height="100%" overflow="auto" p={3}>
                            {loading ? (
                                <Typography>Loading...</Typography>
                            ) : batch ? (
                                <>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                        <Typography variant="h6" fontWeight={700}>
                                            {batch.name}
                                        </Typography>
                                        <Box display="flex" gap={1}>
                                    
                                            <Button variant="outlined" onClick={() => navigate('/batches')}>
                                                Back
                                            </Button>
                                        </Box>
                                    </Box>

                                    {/* Batch details card */}
                                    <Box
                                        sx={{
                                            border: `1px solid ${theme.palette.divider}`,
                                            borderRadius: 2,
                                            mb: 3,
                                            maxWidth: 500,
                                        }}
                                    >
                                        <DetailRow label="Notes" value={batch.notes || '—'} />
                                    </Box>

                                    {/* Active students table */}
                                    <Typography variant="subtitle2" fontWeight={700} mb={1}>
                                        Active Students ({students.length})
                                    </Typography>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        Only students with status <strong>In Progress</strong> are shown here.
                                    </Alert>
                                    <Box
                                        border={`2px solid ${theme.palette.divider}`}
                                        borderRadius={2}
                                        sx={{ borderStyle: 'dashed' }}
                                    >
                                        <Table
                                            columns={studentColumns}
                                            data={studentRows}
                                            export={false}
                                            selection={false}
                                            toolbar
                                            title=""
                                            isRemoteData={false}
                                        />
                                    </Box>
                                </>
                            ) : (
                                <Typography>Batch not found.</Typography>
                            )}
                        </Box>
                    </Box>
                </Box>
            </main>

            {/* Assign Student Dialog */}
            <Dialog open={assignDialog} onClose={() => setAssignDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Assign Student to Batch</DialogTitle>
                <DialogContent>
                    <Box mt={1}>
                        <Select
                            styles={{ menu: (p) => ({ ...p, zIndex: 9999 }) }}
                            placeholder="Search student..."
                            options={allStudents}
                            value={selectedStudent}
                            onChange={setSelectedStudent}
                            isSearchable
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAssignDialog(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleAssign}
                        disabled={!selectedStudent || assigning}
                    >
                        Assign
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ViewBatch;
